/**
 * Живий стан вікна вхідного дзвінка (CALL-10; web-calls.md, розд. 7): без React, крім хука внизу.
 * Подія `calls.ringing` приходить кожному оператору підключення; у першому екрані живе лише цей
 * файл і `IncomingCallHost.tsx`, саме вікно — лінивий чанк.
 *
 * - **Кілька дзвінків** — черга: на екрані перший, розмова людини стає першою.
 * - **Хтось інший відповів** чи дзвінок закінчився без людини — вікно закривається; своя завершена
 *   розмова лишається пропозицією записати результат.
 * - **Свіжість** — за сервером, а не за годинником браузера (рев'ю #1085, Архітектор S3 → #1091):
 *   застарілий `ringing` сервер не публікує, а чужий дзвінок, що вже не дзвонить, `GET …/incoming`
 *   не віддає (404 закриває вікно). Повтор потоку після обриву несе й кінець дзвінка.
 * - **Обрив потоку**: закриття може не прийти, тож кожен стан має свій строк.
 */
import type { CallRingingState, RealtimeEvent } from '@clientline/contracts';
import { useSyncExternalStore } from 'react';

export interface IncomingEntry {
  call: string;
  state: CallRingingState;
  /** Відповіла сама людина. */
  self: boolean;
}

export const ENTRY_TTL_MS: Readonly<Record<CallRingingState, number>> = {
  ringing: 90_000,
  answered: 3_600_000,
  ended: 300_000,
};

let entries: readonly IncomingEntry[] = [];
/** Сховані людиною: вікно не повертається, поки людина сама не відповіла. */
const hidden = new Set<string>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();
const listeners = new Set<() => void>();

function set(next: readonly IncomingEntry[]) {
  entries = next;
  for (const listener of listeners) listener();
}

function remove(call: string) {
  clearTimeout(timers.get(call));
  timers.delete(call);
  if (entries.some((entry) => entry.call === call)) {
    set(entries.filter((entry) => entry.call !== call));
  }
}

function put(entry: IncomingEntry) {
  const rest = entries.filter((item) => item.call !== entry.call);
  const shown = entries[0]?.call === entry.call;
  set(entry.self || shown ? [entry, ...rest] : [...rest, entry]);
  clearTimeout(timers.get(entry.call));
  timers.set(
    entry.call,
    setTimeout(() => remove(entry.call), ENTRY_TTL_MS[entry.state]),
  );
}

/** Подія потоку → стан вікна (експорт — для тестів). */
export function applyIncoming(event: RealtimeEvent): void {
  const { call, state, self } = event.data ?? {};
  if (typeof call !== 'string' || typeof state !== 'string') return;
  const known = entries.find((entry) => entry.call === call);
  const mine = self === true || known?.self === true;
  if (state === 'ended') {
    hidden.delete(call);
    if (mine && known) put({ call, state, self: true });
    else remove(call);
    return;
  }
  if (state === 'answered' && !mine) {
    remove(call);
    return;
  }
  if (state === 'answered') hidden.delete(call);
  if (state !== 'ringing' && state !== 'answered') return;
  if (hidden.has(call)) return;
  put({ call, state, self: mine });
}

/** Людина сховала вікно. */
export function hideIncoming(call: string): void {
  hidden.add(call);
  remove(call);
}

/** Вихід з оболонки (експорт — і для тестів). */
export function resetIncoming(): void {
  for (const timer of timers.values()) clearTimeout(timer);
  timers.clear();
  hidden.clear();
  set([]);
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Дзвінок на екрані. */
export const currentIncoming = (): IncomingEntry | null => entries[0] ?? null;

export const useIncomingCall = (): IncomingEntry | null =>
  useSyncExternalStore(subscribe, currentIncoming, () => null);
